import { Vector3, Vector2, Quaternion, Object3D, Spherical } from "three";
import { IRotateArguments, IRotateControl } from "./rotate-control-interface";
import { CLOSE_TO_ZERO } from "../../../utils/math-utils";

/**
 * minimum arguments needed to perform simple trackball rotation
 */
export interface ICustomRotateArguments extends IRotateArguments {
  /**
   * point to rotate around
   */
  target?: Vector3;
  center?: Vector3;
}

/**
 * projection mode agnostic camera rotator
 *
 * [live demo](https://threejs.org/examples/misc_controls_trackball.html)
 *
 * example:
 * ```typescript
 * const camera = new Quick3dOrthographicCamera();
 * const pointToRotateAround = new Vector3(0,0,0); // origin
 * // set the camera 50 units in front of target and point towards it
 * camera.position.set(0,0,50);
 * camera.lookAt(pointToRotateAround);
 * const rotateControl = new GenericTrackballRotateControl();
 * // rotate horizontally 90 degrees
 * rotateControl.rotate(camera, {horizontalAngle: 1, rotateSpeed: Math.PI * 0.5, target: pointToRotateAround});
 * // rotate vertically 45 degrees
 * rotateControl.rotate(camera, {verticalAngle: 1, panSpeed: Math.PI * 0.25, target: pointToRotateAround});
 * // perform both of the above steps in a single call
 * rotateControl.rotate(camera, {horizontalAngle: Math.PI * 0.5, verticalAngle: Math.PI * 0.25, target: pointToRotateAround});
 * ```
 */
export class GenericCustomRotateControl implements IRotateControl {
  private angleVector: Vector2;
  private axis: Vector3;
  private camSide: Vector3;
  private camUp: Vector3;
  private moveDir: Vector3;
  private previousTarget: Vector3;
  private toCam: Vector3;

  private localX: Vector3;
  private localY: Vector3;
  private localZ: Vector3;
  private offset: Vector3;
  private spherical: Spherical;

  public constructor() {
    this.angleVector = new Vector2();
    this.axis = new Vector3();
    this.camSide = new Vector3();
    this.camUp = new Vector3();
    this.moveDir = new Vector3();
    this.previousTarget = new Vector3();
    this.toCam = new Vector3();

    this.localX = new Vector3();
    this.localY = new Vector3();
    this.localZ = new Vector3();

    this.offset = new Vector3();
    this.previousTarget = new Vector3();
    this.spherical = new Spherical();
  }

  /**
   * rotates camera around target
   * @param rotateArguments.horizontalAngle defaulted to 0
   * @param rotateArguments.rotateSpeed defaulted to 1
   * @param rotateArguments.target defaulted to (0, 0, 0)
   * @param rotateArguments.verticalAngle defaulted to 0
   */
  public rotate(
    camera: Object3D,
    rotateArguments: ICustomRotateArguments
  ): void {
    const {
      horizontalAngle = 0,
      rotateSpeed = 1,
      target = this.previousTarget,
      verticalAngle = 0,
      center = this.previousTarget,
    } = rotateArguments;

    const angle = this.angleVector
      .set(horizontalAngle, verticalAngle)
      .multiplyScalar(rotateSpeed)
      .length();

      if (angle !== 0 || horizontalAngle) {
        // direction to camera from target
        this.toCam.copy(camera.position).sub(target);
        const distanceFromTarget = this.toCam.length();
  
        this.toCam.normalize();
        this.camUp.copy(camera.up);
        this.camSide.crossVectors(this.camUp, camera.getWorldDirection(new Vector3())).normalize();
        const vertAngle = verticalAngle / (Math.abs(verticalAngle) + Math.abs(horizontalAngle)) * angle;
        const horiAngle = horizontalAngle / (Math.abs(verticalAngle) + Math.abs(horizontalAngle)) * angle;
        const vertquaternion = new Quaternion().setFromAxisAngle(this.camSide, -vertAngle);
        const horiQuaternion = new Quaternion().setFromAxisAngle(new Vector3(0, 0, 1), -horiAngle);
        camera.position
              .copy(this.toCam)
              .applyQuaternion(vertquaternion)
              .applyQuaternion(horiQuaternion)
              .multiplyScalar(distanceFromTarget)
              .add(target);
        camera.up
              .applyQuaternion(vertquaternion)
              .applyQuaternion(horiQuaternion);
        const camDirection = new Vector3();
        camera.getWorldDirection(camDirection);
        camDirection.applyQuaternion(vertquaternion)
                    .applyQuaternion(horiQuaternion);
        camera.lookAt(new Vector3().copy(camera.position).add(camDirection));
    }
  }
}
